import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  SUPPLIER_PAYMENT_LABELS,
  SUPPLIER_TYPE_LABELS,
  type SupplierBuyer,
  type SupplierListRow,
} from '../../shared/api/suppliers.api'
import { RatingStars } from './RatingStars'

// Колонки таблицы «Поставщики». Вынесены из SuppliersPage (лимит 500 строк).
// ВАЖНО: CheckboxSetFilter читает значения строго из node.data[field]
// (не valueGetter!) — поэтому все фильтруемые вычисляемые значения
// материализованы в строке через toGridRow (русские метки, как видит юзер).

const NO_RATING = 'Без рейтинга'

/** Строка грида: SupplierListRow + вычисленные метки для фильтров-воронок. */
export interface SupplierGridRow extends SupplierListRow {
  ratingLabel: string
  typeLabel: string
  buyerLabel: string
  paymentLabel: string
  creditLimitLabel: string
  /** Суммарные рекламации: ручное число из файла РОЗ + записи журнала. */
  complaintsTotal: number
}

export function toGridRow(s: SupplierListRow): SupplierGridRow {
  return {
    ...s,
    ratingLabel: s.rating != null ? String(s.rating) : NO_RATING,
    typeLabel: s.type ? SUPPLIER_TYPE_LABELS[s.type] : '—',
    buyerLabel: s.responsibleBuyer?.name ?? s.responsibleBuyer?.email ?? 'Не назначен',
    paymentLabel: s.paymentType ? SUPPLIER_PAYMENT_LABELS[s.paymentType] : '—',
    creditLimitLabel: s.creditLimitAmount
      ? `${Number(s.creditLimitAmount).toLocaleString('ru-RU')} ${s.creditLimitCurrency ?? ''}`.trim()
      : '—',
    complaintsTotal: s.complaintsCount + (s._count?.complaints ?? 0),
  }
}

export function buildSupplierColDefs(params: {
  /**
   * Колонка действий (скрыть / вернуть): роль вообще умеет скрывать. Кнопка в
   * строке — только если сервер разрешил именно этого поставщика.
   */
  showActions: boolean
  showHidden: boolean
  buyers: SupplierBuyer[]
  onOpen: (id: string) => void
  onAssign: (id: string, buyerId: string | null) => void
  onHide: (row: SupplierGridRow) => void
  onRestore: (row: SupplierGridRow) => void
}): ColDef<SupplierGridRow>[] {
  const { showActions, showHidden, buyers, onOpen, onAssign, onHide, onRestore } = params
  return [
    {
      field: 'name',
      headerName: 'Поставщик',
      minWidth: 200,
      flex: 1,
      cellRenderer: (p: ICellRendererParams<SupplierGridRow>) => {
        const s = p.data!
        return (
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <button
              onClick={() => onOpen(s.id)}
              className="text-brand-600 hover:text-brand-700 font-medium truncate"
              title="Карточка поставщика: статистика, бренды, заявки, рекламации"
            >
              {s.name}
            </button>
            {s.aliases.length > 0 && (
              <span
                className="shrink-0 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px]"
                title={`Объединён с: ${s.aliases.map((a) => a.aliasName).join(', ')}`}
              >
                +{s.aliases.length}
              </span>
            )}
          </span>
        )
      },
    },
    {
      field: 'ratingLabel',
      headerName: 'Рейтинг',
      minWidth: 110,
      comparator: (a: string, b: string) => (Number(a) || 0) - (Number(b) || 0),
      cellRenderer: (p: ICellRendererParams<SupplierGridRow>) => (
        <RatingStars value={p.data!.rating} />
      ),
    },
    {
      field: 'quotesGiven',
      headerName: 'Квот',
      minWidth: 90,
      type: 'rightAligned',
      cellRenderer: (p: ICellRendererParams<SupplierGridRow>) => {
        const n = p.data!.quotesGiven
        return n ? (
          <span>{n.toLocaleString('ru-RU')}</span>
        ) : (
          <span className="text-gray-400">0</span>
        )
      },
    },
    {
      field: 'wonItems',
      headerName: 'В счёт',
      minWidth: 90,
      type: 'rightAligned',
      cellRenderer: (p: ICellRendererParams<SupplierGridRow>) => {
        const n = p.data!.wonItems
        return n ? (
          <span className="text-emerald-700 font-medium">{n.toLocaleString('ru-RU')}</span>
        ) : (
          <span className="text-gray-400">0</span>
        )
      },
    },
    {
      field: 'typeLabel',
      headerName: 'Тип',
      minWidth: 130,
    },
    {
      field: 'buyerLabel',
      headerName: 'Ответственный закупщик',
      minWidth: 200,
      cellRenderer: (p: ICellRendererParams<SupplierGridRow>) => {
        const s = p.data!
        if (!s.permissions.editRatingAndBuyer) {
          return <span>{s.responsibleBuyer?.name ?? s.responsibleBuyer?.email ?? '—'}</span>
        }
        // Инлайн-назначение «кто чей поставщик» (управляет РОЗ/ADMIN).
        return (
          <select
            value={s.responsibleBuyerId ?? ''}
            onChange={(e) => onAssign(s.id, e.target.value || null)}
            className="w-full px-1 py-0.5 border border-gray-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">Не назначен (видит тот, кто занёс)</option>
            {buyers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || b.email}
              </option>
            ))}
          </select>
        )
      },
    },
    {
      field: 'paymentLabel',
      headerName: 'Условия оплаты',
      minWidth: 170,
    },
    {
      field: 'creditLimitLabel',
      headerName: 'Лимит',
      minWidth: 110,
    },
    {
      field: 'postShipmentDays',
      headerName: 'Отсрочка, дн.',
      minWidth: 90,
      valueFormatter: (p) => (p.value != null ? String(p.value) : '—'),
    },
    {
      field: 'warrantyDays',
      headerName: 'Гарантия, дн.',
      minWidth: 110,
      valueFormatter: (p) => (p.value != null ? String(p.value) : '—'),
    },
    {
      field: 'complaintsTotal',
      headerName: 'Рекламации',
      minWidth: 110,
      cellRenderer: (p: ICellRendererParams<SupplierGridRow>) => {
        const n = p.data!.complaintsTotal
        if (!n) return <span className="text-gray-400">0</span>
        return (
          <span
            className="inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs"
            title={p.data!.complaintsNotes ?? undefined}
          >
            {n}
          </span>
        )
      },
    },
    ...(showActions
      ? [
          {
            colId: 'actions',
            headerName: '',
            minWidth: 90,
            maxWidth: 90,
            sortable: false,
            filter: false,
            cellRenderer: (p: ICellRendererParams<SupplierGridRow>) =>
              !p.data!.permissions.hide ? null : showHidden ? (
                <button
                  onClick={() => onRestore(p.data!)}
                  title="Вернуть в справочник"
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Вернуть
                </button>
              ) : (
                <button
                  onClick={() => onHide(p.data!)}
                  title="Скрыть из справочника (квоты и заявки не пострадают)"
                  className="text-gray-400 hover:text-red-600"
                >
                  ×
                </button>
              ),
          } satisfies ColDef<SupplierGridRow>,
        ]
      : []),
  ]
}
